import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Home, FilePlus2, ClipboardList, LogOut, Truck, Inbox } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { ToastHost } from '@/components/layout/ToastHost';
import { demandesApi } from '@/api/demandes';

/**
 * SIMG-ACHATS-LEADBETON-2026-002 — Layout mobile-first portail acheteur.
 * Miroir de CommercialLayout, palette identique — cohérence UX SIM Group.
 */
export const AchatsLayout = () => {
  const nav  = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Compteur pour le pastille de l'onglet Demandes — refresh doux toutes les 60s
  // pour que l'acheteur voie une nouvelle demande arriver sans reload.
  const { data: compteurs } = useQuery({
    queryKey:        ['achats', 'demandes', 'compteurs'] as const,
    queryFn:         demandesApi.compteurs,
    staleTime:       15_000,
    refetchInterval: 60_000,
  });
  const nbATraiter = (compteurs?.soumises ?? 0) + (compteurs?.urgentes ?? 0);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-2, #F5F7FA)', color: 'var(--tx-1, #111827)',
    }}>
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--bd-1, #E5E7EB)',
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: '#0F766E',
            color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14,
          }}>LB</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Achats · LEAD BETON</div>
            <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>{user?.fullName}</div>
          </div>
        </div>
        <button
          onClick={() => { logout(); nav('/login'); }}
          aria-label="Se déconnecter"
          style={{
            background: 'transparent', border: '1px solid var(--bd-1, #E5E7EB)',
            borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--tx-2, #374151)',
            fontSize: 13,
          }}
        >
          <LogOut size={14} /> Sortir
        </button>
      </header>

      <main style={{
        flex: 1, padding: '16px 16px 96px 16px',
        maxWidth: 900, width: '100%', margin: '0 auto',
      }}>
        <Outlet />
      </main>

      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1px solid var(--bd-1, #E5E7EB)',
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
        boxShadow: '0 -6px 20px -8px rgba(0,0,0,0.15)',
        zIndex: 20,
      }}>
        <NavItem to="/achats"              icon={<Home size={18} />}          label="Aujourd'hui" end />
        <NavItem to="/achats/demandes"     icon={<Inbox size={18} />}         label="Demandes" badge={nbATraiter} />
        <NavItem to="/achats/nouveau"      icon={<FilePlus2 size={18} />}     label="Nouveau BCA" />
        <NavItem to="/achats/bca"          icon={<ClipboardList size={18} />} label="Mes BCA" />
        <NavItem to="/achats/fournisseurs" icon={<Truck size={18} />}         label="Fournisseurs" />
      </nav>

      <ToastHost />
    </div>
  );
};

function NavItem({ to, icon, label, end, badge }: {
  to: string; icon: React.ReactNode; label: string; end?: boolean; badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '10px 4px 12px 4px', gap: 2,
        color: isActive ? '#0F766E' : 'var(--tx-3, #6B7280)',
        fontSize: 11, fontWeight: isActive ? 700 : 500,
        textDecoration: 'none',
        borderTop: isActive ? '2px solid #0F766E' : '2px solid transparent',
        position: 'relative',
      })}
    >
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        {icon}
        {badge !== undefined && badge > 0 && (
          <span style={{
            position: 'absolute', top: -6, right: -10,
            background: '#DC2626', color: '#fff',
            fontSize: 10, fontWeight: 700, lineHeight: 1,
            padding: '2px 5px', borderRadius: 10,
            minWidth: 16, textAlign: 'center',
          }}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span>{label}</span>
    </NavLink>
  );
}
